import React, { memo } from "react";

export const PAGE_SIZES = [25, 50, 100];

/** Page numbers around the current one, with gaps: 1 … 4 5 [6] 7 8 … 20 */
function pageWindow(page: number, pages: number): (number | "gap")[] {
  const out: (number | "gap")[] = [];
  const from = Math.max(2, page - 2);
  const to = Math.min(pages - 1, page + 2);
  out.push(1);
  if (from > 2) out.push("gap");
  for (let p = from; p <= to; p++) out.push(p);
  if (to < pages - 1) out.push("gap");
  if (pages > 1) out.push(pages);
  return out;
}

/**
 * Footer for server-paged lists: "Showing 26–50 of 312", page size, and page
 * buttons. Wraps onto two lines on phones; page numbers collapse to Prev/Next
 * below the sm breakpoint.
 */
function Pager({
  page,
  limit,
  total,
  onPage,
  onLimit,
  sizes = PAGE_SIZES,
  disabled = false,
}: {
  page: number;
  limit: number;
  total: number;
  onPage: (page: number) => void;
  onLimit?: (limit: number) => void;
  sizes?: number[];
  disabled?: boolean;
}) {
  const pages = Math.max(1, Math.ceil(total / limit));
  const first = total === 0 ? 0 : (page - 1) * limit + 1;
  const last = Math.min(total, page * limit);
  const go = (p: number) => !disabled && p >= 1 && p <= pages && p !== page && onPage(p);

  return (
    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-2 border-top small">
      <div className="text-muted">
        {total === 0 ? "No results" : <>Showing <b>{first}–{last}</b> of <b>{total}</b></>}
      </div>

      <div className="d-flex align-items-center gap-2 flex-wrap">
        {onLimit && (
          <select
            className="form-select form-select-sm"
            style={{ width: "auto" }}
            value={limit}
            disabled={disabled}
            onChange={(e) => onLimit(Number(e.target.value))}
            aria-label="Rows per page"
          >
            {sizes.map((s) => <option key={s} value={s}>{s} / page</option>)}
          </select>
        )}

        <nav aria-label="Pagination">
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${page <= 1 || disabled ? "disabled" : ""}`}>
              <button type="button" className="page-link" onClick={() => go(page - 1)} aria-label="Previous page">‹</button>
            </li>
            {pageWindow(page, pages).map((p, i) =>
              p === "gap" ? (
                <li key={`gap${i}`} className="page-item disabled d-none d-sm-block"><span className="page-link">…</span></li>
              ) : (
                <li key={p} className={`page-item d-none d-sm-block ${p === page ? "active" : ""}`}>
                  <button type="button" className="page-link" onClick={() => go(p)} aria-current={p === page ? "page" : undefined}>{p}</button>
                </li>
              )
            )}
            {/* phones: just "3 / 12" between the arrows */}
            <li className="page-item disabled d-sm-none"><span className="page-link">{page} / {pages}</span></li>
            <li className={`page-item ${page >= pages || disabled ? "disabled" : ""}`}>
              <button type="button" className="page-link" onClick={() => go(page + 1)} aria-label="Next page">›</button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
}

export default memo(Pager);
